import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig, debounce } from '@theme/utilities';

/**
 * A custom element that shows an "Add to cart" button until the product
 * is added to the cart, then morphs it into a quantity stepper. Reducing
 * the quantity to zero removes the item from the cart and morphs the
 * stepper back into the "Add to cart" button.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} quantityInput - The quantity input.
 *
 * @extends {Component<Refs>}
 */
class ProductCardAddToCart extends Component {
  requiredRefs = ['quantityInput'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd);
  }

  /**
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    if (!(event instanceof CartAddEvent) || event.detail.data?.didError) return;

    const added = Number(event.detail.data?.itemCount) || 1;
    this.#setQuantity((Number(this.refs.quantityInput.value) || 0) + added);
  };

  increaseQuantity() {
    this.#changeQuantity(1);
  }

  decreaseQuantity() {
    this.#changeQuantity(-1);
  }

  /**
   * @param {number} delta
   */
  #changeQuantity(delta) {
    const quantity = Math.max(0, (Number(this.refs.quantityInput.value) || 0) + delta);
    this.#setQuantity(quantity);
    this.#queueCartUpdate(quantity);
  }

  #queueCartUpdate = debounce((quantity) => this.#updateCart(quantity), 300);

  /**
   * @param {number} quantity
   */
  async #updateCart(quantity) {
    const body = JSON.stringify({ id: this.dataset.variantId, quantity });

    try {
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const cart = await response.json();

      if (cart.errors) {
        this.#setQuantity(Number(this.dataset.cartQuantity) || 0);
        return;
      }

      const item = cart.items?.find((item) => item.variant_id.toString() === this.dataset.variantId);

      this.#setQuantity(item ? item.quantity : 0);

      document.dispatchEvent(
        new CartUpdateEvent(cart, this.dataset.variantId ?? '', {
          itemCount: cart.item_count,
          source: 'product-card-add-to-cart',
          variantId: this.dataset.variantId,
        })
      );
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {number} quantity
   */
  #setQuantity(quantity) {
    this.dataset.cartQuantity = String(quantity);
    this.refs.quantityInput.value = String(quantity);
    this.toggleAttribute('data-in-cart', quantity > 0);
  }
}

if (!customElements.get('product-card-add-to-cart-component')) {
  customElements.define('product-card-add-to-cart-component', ProductCardAddToCart);
}
